/**
 * Client helpers for POST/GET/DELETE /api/settlement/:estateId/documents
 * (phase + stepId scoped settlement document storage).
 */

import { API_BASE_URL } from '../config/api';

export interface SettlementDocumentListItem {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  createdAt: string;
  description?: string | null;
  stepId?: string;
  phase?: string;
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('account-token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function listSettlementDocuments(
  estateId: string,
  phase: string,
  stepId: string
): Promise<SettlementDocumentListItem[]> {
  const q = new URLSearchParams({ phase, stepId });
  const res = await fetch(`${API_BASE_URL}/api/settlement/${estateId}/documents?${q}`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to list documents');
  const data = await res.json();
  if (!data.success || !Array.isArray(data.documents)) return [];
  return data.documents.map((d: Record<string, unknown>) => ({
    id: String(d.id),
    filename: String(d.filename ?? ''),
    mimeType: String(d.mimeType ?? ''),
    size: Number(d.size ?? 0),
    createdAt: String(d.createdAt ?? ''),
    description: d.description != null ? String(d.description) : null,
    stepId: d.stepId != null ? String(d.stepId) : undefined,
    phase: d.phase != null ? String(d.phase) : undefined,
  }));
}

export async function uploadSettlementDocument(
  estateId: string,
  phase: string,
  stepId: string,
  file: File,
  description?: string
): Promise<SettlementDocumentListItem> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
  const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;

  const res = await fetch(`${API_BASE_URL}/api/settlement/${estateId}/documents`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
      data: base64,
      phase,
      stepId,
      description: description ?? null,
    }),
  });
  if (!res.ok) throw new Error('Upload failed');
  const json = await res.json();
  if (!json.success || !json.document) throw new Error('Invalid upload response');
  const doc = json.document;
  return {
    id: String(doc.id),
    filename: String(doc.filename ?? file.name),
    mimeType: String(doc.mimeType ?? file.type),
    size: Number(doc.size ?? file.size),
    createdAt: String(doc.createdAt ?? new Date().toISOString()),
  };
}

export async function deleteSettlementDocument(estateId: string, documentId: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/settlement/${estateId}/documents/${documentId}`, {
    method: 'DELETE',
    headers: authHeaders(),
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Delete failed');
}
